import type { FormEvent } from "react";

import { REQUIRED_MESSAGE, useDialogForm } from "@/ds";
import type { Permission } from "@/modules/permissions";
import type { Role } from "@/modules/roles";
import { isResourceRole, RESOURCE_ROLE_NAME_HINT } from "../../roles.constants";
import { CREATE_ROLE_TITLE, EDIT_ROLE_TITLE, ROLE_FORM_ID } from "./role-form-dialog.constants";
import type {
  PermissaoLinha,
  PermissoesEstado,
  RoleFormDialogProps,
  RoleFormDialogViewModel,
  RoleFormState,
} from "./types-role-form-dialog";

function validate({ nome }: RoleFormState) {
  return nome.trim() ? {} : { nome: REQUIRED_MESSAGE };
}

/**
 * Chaves das permissões já associadas ao perfil. Guarda id e código: no perfil o id é a própria claim, no catálogo é o
 * registro; a marcação casa pelos dois.
 */
function chavesIniciais(role: Role | null): string[] {
  return (role?.permissoes ?? []).flatMap((permission) =>
    [permission.id, permission.codigo].filter(Boolean),
  );
}

function estaMarcada(marcadas: string[], permission: Permission): boolean {
  return (
    marcadas.includes(permission.id) ||
    (!!permission.codigo && marcadas.includes(permission.codigo))
  );
}

/**
 * Formulário Novo/Editar perfil (HU 864): nome obrigatório validado em tela antes de qualquer requisição (CA3) e o
 * conjunto completo de permissões marcado (RN02). O código é gerado pelo serviço e não aparece no modal (Figma
 * 4434:111717); a unicidade do nome vem do 409 do serviço.
 */
export function useRoleFormDialog({
  role,
  permissions,
  isLoadingPermissions,
  hasPermissionsError,
  onSubmit,
  onCancel,
}: RoleFormDialogProps): RoleFormDialogViewModel {
  const form = useDialogForm<RoleFormState>({
    initial: () => ({ nome: role?.nome ?? "", marcadas: chavesIniciais(role) }),
    validate,
    onSubmit: (values) =>
      onSubmit({
        nome: values.nome.trim(),
        claims: permissions
          .filter((permission) => estaMarcada(values.marcadas, permission))
          .map((permission) => permission.codigo),
      }),
  });

  const isNameLocked = isResourceRole(role);
  const estado: PermissoesEstado = isLoadingPermissions
    ? "carregando"
    : hasPermissionsError
      ? "erro"
      : permissions.length === 0
        ? "vazio"
        : "lista";

  const linhas: PermissaoLinha[] = permissions.map((permission) => ({
    id: permission.id,
    nome: permission.nome || permission.codigo,
    descricao: permission.descricao,
    marcada: estaMarcada(form.values.marcadas, permission),
    testId: `role-form-permission-${permission.id}`,
  }));

  function alternar(id: string) {
    const permission = permissions.find((item) => item.id === id);
    if (!permission) return;

    const { marcadas } = form.values;
    form.setValue(
      "marcadas",
      estaMarcada(marcadas, permission)
        ? marcadas.filter((chave) => chave !== permission.id && chave !== permission.codigo)
        : [...marcadas, permission.id],
    );
  }

  return {
    formId: ROLE_FORM_ID,
    titulo: role ? EDIT_ROLE_TITLE : CREATE_ROLE_TITLE,
    nome: {
      value: form.values.nome,
      error: form.errors.nome ?? null,
      // O nome do perfil "Recurso" é fixo: o serviço também recusa a alteração.
      bloqueado: isNameLocked,
      dica: isNameLocked ? RESOURCE_ROLE_NAME_HINT : undefined,
      digitar: (texto) => form.setValue("nome", texto),
    },
    permissoes: { estado, linhas, alternar },
    isSubmitting: form.isSubmitting,
    podeSalvar: estado !== "carregando" && estado !== "erro",
    enviar: (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      void form.submit();
    },
    cancelar: onCancel,
  };
}
